// Renders typed rich text and resolves content registry links.

import React from 'react';
import { Link } from 'react-router';
import {
  resolveCollectionHref,
  resolveDocumentHref,
  type RichTextNode,
} from '@airhead-games/shared/content';
import { cn } from '@/utilities/cn';

interface RichTextRendererProps {
  nodes?: RichTextNode[];
  className?: string;
}

const linkClassName = 'text-primary underline-offset-4 hover:underline';

const isExternalHref = (href: string) =>
  href.startsWith('http://') ||
  href.startsWith('https://') ||
  href.startsWith('mailto:');

const renderNodes = (nodes: RichTextNode[] | undefined): React.ReactNode =>
  nodes?.map((node, index) => {
    if (node.type === 'text') {
      return <React.Fragment key={index}>{node.text}</React.Fragment>;
    }

    if (node.type === 'strong') {
      return <strong key={index}>{renderNodes(node.children)}</strong>;
    }

    if (node.type === 'em') {
      return <em key={index}>{renderNodes(node.children)}</em>;
    }

    if (node.type === 'docLink') {
      const href = resolveDocumentHref(node.documentId);
      return href ? (
        <Link key={index} to={href} className={linkClassName}>
          {renderNodes(node.children)}
        </Link>
      ) : (
        <React.Fragment key={index}>
          {renderNodes(node.children)}
        </React.Fragment>
      );
    }

    if (node.type === 'collectionLink') {
      const href = resolveCollectionHref(node.collectionId);
      return href ? (
        <Link key={index} to={href} className={linkClassName}>
          {renderNodes(node.children)}
        </Link>
      ) : (
        <React.Fragment key={index}>
          {renderNodes(node.children)}
        </React.Fragment>
      );
    }

    if (isExternalHref(node.href)) {
      const isMailto = node.href.startsWith('mailto:');
      return (
        <a
          key={index}
          href={node.href}
          className={linkClassName}
          target={isMailto ? undefined : '_blank'}
          rel={isMailto ? undefined : 'noopener noreferrer'}
        >
          {renderNodes(node.children)}
        </a>
      );
    }

    return (
      <Link key={index} to={node.href} className={linkClassName}>
        {renderNodes(node.children)}
      </Link>
    );
  });

const RichTextRenderer: React.FC<RichTextRendererProps> = ({
  nodes,
  className,
}) => <span className={cn(className)}>{renderNodes(nodes)}</span>;

export default RichTextRenderer;
export { renderNodes as renderRichTextNodes };
